import React, { useState } from 'react';
import api from '../api/axios';

function AddJobPage({ onJobAdded, setCurrentPage, returnPage = 'home' }) {
  const [deviceType, setDeviceType] = useState(null); // null, '3d', or 'laser'
  const [formData, setFormData] = useState({
    notes: '',
    material: ''
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [printForMe, setPrintForMe] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const laserFileExtensions = ['.ai', '.svg', '.dwg', '.dxf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach(file => {
      const fileName = file.name.toLowerCase();
      let isValid = false;

      if (deviceType === '3d' && (fileName.endsWith('.stl') || fileName.endsWith('.zip') || fileName.endsWith('.3mf') || fileName.endsWith('.obj'))) {
        isValid = true;
      } else if (deviceType === 'laser' && laserFileExtensions.some(ext => fileName.endsWith(ext))) {
        isValid = true;
      }

      if (isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      const ext = deviceType === '3d' ? 'STL, 3MF, OBJ, or ZIP' : 'AI, SVG, DWG, DXF, JPG, PNG, GIF, BMP, or WEBP';
      setError(`Invalid files: ${invalidFiles.join(', ')}. Please select ${ext} files.`);
    }
    
    if (validFiles.length > 0) {
      setFiles(validFiles);
      setError('');
    } else {
      setFiles([]);
    }
  };

  const handleSubmit3DJob = async (e) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one STL, 3MF, OBJ, or ZIP file');
      return;
    }

    setSubmitting(true);
    const uploadFormData = new FormData();
    files.forEach(file => {
      uploadFormData.append('stlFiles', file);
    });
    uploadFormData.append('notes', formData.notes);
    uploadFormData.append('printForUser', String(printForMe));

    try {
      await api.post('/jobs', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onJobAdded();
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitLaserJob = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      setError('Please select a design file');
      return;
    }

    setSubmitting(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', files[0]);
    uploadFormData.append('notes', formData.notes);
    uploadFormData.append('printForUser', String(printForMe));

    try {
      await api.post('/laser-jobs', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onJobAdded();
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding laser/other job');
    } finally {
      setSubmitting(false);
    }
  };

  // Device type selection screen
  if (!deviceType) {
    return (
      <div className="form-section">
        <button
          onClick={() => setCurrentPage && setCurrentPage(returnPage)}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back
        </button>
        <h2>What would you like to make?</h2>
        <p style={{ color: '#666', marginBottom: '30px' }}>Select your device type to get started</p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <button
            onClick={() => setDeviceType('3d')}
            style={{
              padding: '40px 20px',
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196F3',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2196F3',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#bbdefb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#e3f2fd'}
          >
            ⬛ 3D Print
          </button>
          
          <button
            onClick={() => setDeviceType('laser')}
            style={{
              padding: '40px 20px',
              backgroundColor: '#fff3e0',
              border: '2px solid #FF9800',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#FF9800',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#ffe0b2'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#fff3e0'}
          >
            ⚡ Laser/Other
          </button>
        </div>
      </div>
    );
  }

  // 3D Printer Job Form
  if (deviceType === '3d') {
    return (
      <div className="form-section">
        <button
          onClick={() => {
            setDeviceType(null);
            setFiles([]);
            setError('');
          }}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back
        </button>
        
        <h2>⬛ Add 3D Print Job</h2>
        {error && <div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}
        
        <form onSubmit={handleSubmit3DJob}>
          <div className="form-group">
            <label>Model File(s) *</label>
            <input
              type="file"
              accept=".stl,.zip,.3mf,.obj"
              onChange={handleFileChange}
              multiple
              required
            />
            {files.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                <strong>Files selected ({files.length}):</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {files.map((f, i) => (
                    <li key={i}>{f.name}</li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              💡 <strong>Have multiple STL files?</strong> Zip them on your computer first, then upload the .zip file here.
            </p>
          </div>

          <div className="form-group">
            <label>Special Instructions</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Please specify any important details: filament type (PLA, ABS, PETG, TPU), color preference, nozzle diameter (0.4, 0.6, 0.8, 1.0mm), supports needed, infill level, or any other requirements. If you don't have specific preferences, our staff will make their best judgment."
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '120px', fontFamily: 'Arial, sans-serif' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: printForMe ? '#e8f5e9' : '#f5f5f5', borderRadius: '6px', border: `1px solid ${printForMe ? '#a5d6a7' : '#ddd'}` }}>
            <input
              type="checkbox"
              id="printForMe"
              checked={printForMe}
              onChange={e => setPrintForMe(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="printForMe" style={{ cursor: 'pointer', margin: 0 }}>
              <strong>{printForMe ? 'Please print this for me' : 'I will print this myself'}</strong>
              <span style={{ display: 'block', fontSize: '12px', color: '#666' }}>
                {printForMe ? 'A makerspace tech will handle the printing.' : "Check if you'd like someone to print this for you."}
              </span>
            </label>
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Adding Job...' : 'Add 3D Print Job'}
          </button>
        </form>
      </div>
    );
  }

  // Laser Cutter Job Form
  if (deviceType === 'laser') {
    return (
      <div className="form-section">
        <button
          onClick={() => {
            setDeviceType(null);
            setFiles([]);
            setError('');
          }}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back
        </button>
        
        <h2>⚡ Add Laser/Other Job</h2>
        {error && <div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}
        
        <form onSubmit={handleSubmitLaserJob}>
          <div className="form-group">
            <label>Design File *</label>
            <input
              type="file"
              accept=".ai,.svg,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
              onChange={handleFileChange}
              required
            />
            {files.length > 0 && <p style={{ fontSize: '12px', color: '#666' }}>File: {files[0].name}</p>}
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              💡 <strong>AI, SVG, DWG, DXF, JPG, PNG, GIF, BMP, or WEBP</strong> accepted for laser/other jobs.
            </p>
          </div>

          <div className="form-group">
            <label>Special Instructions</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any special requests or notes about your design"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px', fontFamily: 'Arial, sans-serif' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: printForMe ? '#e8f5e9' : '#f5f5f5', borderRadius: '6px', border: `1px solid ${printForMe ? '#a5d6a7' : '#ddd'}` }}>
            <input
              type="checkbox"
              id="printForMeLaser"
              checked={printForMe}
              onChange={e => setPrintForMe(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="printForMeLaser" style={{ cursor: 'pointer', margin: 0 }}>
              <strong>{printForMe ? 'Please cut this for me' : 'I will operate the laser cutter myself'}</strong>
              <span style={{ display: 'block', fontSize: '12px', color: '#666' }}>
                {printForMe ? 'A makerspace tech will handle the cutting.' : "Check if you'd like someone to cut this for you."}
              </span>
            </label>
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Submitting Job...' : 'Submit Laser/Other Job'}
          </button>
        </form>
      </div>
    );
  }
}

export default AddJobPage;
